import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Flex,
  Heading,
  Button,
  Image,
  UnorderedList,
  ListItem,
  Text,
  Box,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useRef } from 'react';

import bath from '../../assets/images/pass_examples/bath.png';
import bicester from '../../assets/images/pass_examples/bicester.png';
import pavilion from '../../assets/images/pass_examples/pavilion.png';
import shy from '../../assets/images/pass_examples/shy.png';
import transit from '../../assets/images/pass_examples/transit.png';
import wilde from '../../assets/images/pass_examples/wilde.png';
import { useAppSelector } from '../../store/redux/hooks';
import StampCardInspirationModal from '../client/pass-templates-new/modals/StampCardInspirationModal';

const InspirationModal = ({ isOpen, onClose }) => {
  const images = [bicester, bath, pavilion, transit, shy, wilde];
  const scrollRef = useRef<HTMLDivElement>(null);
  const templateType = useAppSelector((s) => s.template.templateType);

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 300, behavior: 'smooth' });
  };

  return (
    <>
      {templateType === 'stampCard' ? (
        <StampCardInspirationModal isOpen={isOpen} onClose={onClose} />
      ) : (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Inspiration Gallery</ModalHeader>
            <ModalCloseButton />
            <ModalBody p={2}>
              <Flex justifyContent="space-between" alignItems="center" px={2}>
                {/* LEFT BUTTON */}
                <Box as="button" onClick={scrollLeft} cursor="pointer">
                  <ChevronLeftIcon width="40px" height="40px" />
                </Box>

                {/* SCROLLABLE ROW */}
                <Box
                  ref={scrollRef}
                  overflowX="auto"
                  flex="1"
                  px={2}
                  py={4}
                  minWidth="0"
                  scrollSnapType="x mandatory"
                  sx={{
                    '::-webkit-scrollbar': { display: 'none' },
                  }}
                  style={{
                    scrollbarWidth: 'none',
                  }}
                >
                  <Flex gap={3} width="max-content">
                    {images.map((src, i) => (
                      <Box key={i} scrollSnapAlign="center">
                        <Image
                          src={src}
                          height="200px"
                          objectFit="contain"
                          userSelect="none"
                          transition="transform 0.25s ease, box-shadow 0.25s ease"
                          _hover={{
                            transform: 'scale(1.06)',
                            boxShadow: 'xl',
                          }}
                        />
                      </Box>
                    ))}
                  </Flex>
                </Box>

                {/* RIGHT BUTTON */}
                <Box as="button" onClick={scrollRight} cursor="pointer">
                  <ChevronRightIcon width="40px" height="40px" />
                </Box>
              </Flex>

              {/* BEST PRACTICES SECTION */}
              <Box px={10}>
                <Heading mt={6}>Design ideas & best practices</Heading>
                <UnorderedList mt={4} spacing={3}>
                  <ListItem>
                    <Text fontWeight="bold">Keep text short and scannable</Text>
                    <Text>
                      Passes are viewed quickly. Use short labels and keep
                      titles under ~20 characters.
                    </Text>
                  </ListItem>

                  <ListItem>
                    <Text fontWeight="bold">Choose high-contrast colours</Text>
                    <Text>
                      Make sure background vs. text is accessible in bright
                      daylight.
                    </Text>
                  </ListItem>

                  <ListItem>
                    <Text fontWeight="bold">Use high-quality images</Text>
                    <Text>
                      Logos and banners look best as transparent PNGs. Avoid
                      low-resolution uploads.
                    </Text>
                  </ListItem>

                  <ListItem>
                    <Text fontWeight="bold">Prioritise essential info</Text>
                    <Text>
                      Place key information on the front. Move secondary details
                      and links to the back of the pass.
                    </Text>
                  </ListItem>

                  <ListItem>
                    <Text fontWeight="bold">Test on both platforms</Text>
                    <Text>
                      Apple and Google Wallet render passes differently — always
                      preview both.
                    </Text>
                  </ListItem>
                </UnorderedList>
              </Box>
            </ModalBody>
            <ModalFooter justifyContent="flex-end" gap={4}>
              <Button variant="secondary" onClick={onClose} size="sm">
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default InspirationModal;
